<script>
import _ from 'lodash'
import MottoIcon from '@components/motto-icon.vue'
import { authComputed } from '@state/helpers'
import SidebarItem from './sidebar-item.vue'

export default {
  components: {
    SidebarItem,
    MottoIcon,
  },
  props: {
    sidebarExpanded: {
      type: Boolean,
    },
  },
  data() {
    return {
      sidebarRoutes: [
        {
          name: 'home',
          title: 'Home',
        },
        {
          name: 'apps',
          title: 'Apps',
        },
        {
          name: 'resources',
          title: 'Resources',
        },
        {
          name: 'help',
          title: 'Help',
        },
        {
          name: 'awards',
          title: 'Awards',
          routeLabel: 'awards-group',
        },
        {
          name: 'network-connect',
          title: 'Network Connect',
          icon: 'Connection',
        },
      ],
      sidebarSubRoutes: [
        {
          name: 'awards',
          title: 'Awards',
          parentRouteLabel: 'awards-group',
        },
        {
          name: 'my-awards',
          title: 'My Awards',
          parentRouteLabel: 'awards-group',
        },
        {
          name: 'leaderboards',
          title: 'Leaderboards',
          parentRouteLabel: 'awards-group',
        },
        {
          name: 'my-rankings',
          title: 'My Rankings',
          parentRouteLabel: 'awards-group',
        },
      ],
      sidebarOffsetY: 0,
    }
  },
  computed: {
    ...authComputed,
    routeSidebarItemName() {
      return this.$route.meta?.sidebarItemName
    },
    routeSubSidebarItemName() {
      return this.$route.meta?.subSidebarItemName
    },
    getSidebarRoutes() {
      let sidebarRoutes = this.sidebarRoutes
      if (process.env.APP_ENV === 'prod') {
        sidebarRoutes = sidebarRoutes.filter((route) => !route.devOnly)
      }

      if (!this.canManageNetworkConnect) {
        sidebarRoutes = sidebarRoutes.filter((route) => route.name !== 'network-connect')
      }

      return [...sidebarRoutes]
    },
  },
  methods: {
    getSubRoutes({ routeLabel }) {
      return _.filter(this.sidebarSubRoutes, { parentRouteLabel: routeLabel })
    },
  },
}
</script>

<template>
  <div
    :class="{
      [$style.sidebarContainer]: true,
      [$style.isExpanded]: sidebarExpanded,
    }">
    <Transition name="fade">
      <div v-if="sidebarExpanded" :class="$style.expandBackground"></div>
    </Transition>
    <div
      :class="{
        [$style.container]: true,
        [$style.sidebarExpanded]: sidebarExpanded,
      }"
      data-sidebar>
      <div :class="$style.logoContainer">
        <BaseLink href="/" target="_self">
          <img :class="$style.sidebarLogo" src="@assets/images/mottocenter-new-logo-blue.png" alt="Logo" />
          <img :class="$style.sidebarLogoMobile" src="@assets/images/mottocenter-new-logo-blue.png" alt="Logo" />
        </BaseLink>
        <MottoIcon
          :class="$style.mobileHamburgerClose"
          name="HamburgerClose"
          icon-color="primary"
          @click.native="$emit('collapse')" />
      </div>
      <SidebarItem
        v-for="sidebarRoute in getSidebarRoutes"
        :key="sidebarRoute.title"
        :route="sidebarRoute"
        :label="sidebarRoute.title"
        :icon="sidebarRoute.icon || sidebarRoute.title"
        :active="sidebarRoute.title === routeSidebarItemName"
        :selected-sub-sidebar-item="routeSubSidebarItemName"
        :sub-routes="getSubRoutes(sidebarRoute)" />
    </div>
  </div>
</template>

<style lang="scss" module>
@import '@design';

.sidebarContainer {
  @media (max-width: $lg-breakpoint) {
    position: absolute;
    z-index: 10000;
    pointer-events: none;
  }

  z-index: 100;
  pointer-events: auto;
}

.isExpanded {
  pointer-events: auto;
}

.container {
  @media (max-width: $lg-breakpoint) {
    z-index: 100;
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    height: 100vh;
    border: none;
    transition: all 0.3s linear;
    transform: translateX(-300px);
    &.sidebarExpanded {
      position: fixed;
      min-width: 263px;
      max-width: 263px;
      overflow-y: auto;
      transition: all 0.3s linear;
      transform: translateX(0);
    }
  }

  position: fixed;
  z-index: 100;
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  width: 273px;
  max-width: 273px;
  height: calc(100% - 20px);
  min-height: calc(100vh - 20px);
  padding-bottom: 20px;
  margin-bottom: 20px;
  overflow: hidden;
  overflow-y: auto;
  background: $lightest-gray;
  border: 1px solid $lightest-gray;
  border-radius: 0 0 56px 0;
  box-shadow: 0 0 24px rgba(196, 196, 196, 0.3);
}

.logoContainer {
  display: flex;
  justify-content: space-between;

  a {
    @media (max-width: $lg-breakpoint) {
      margin: 0;
    }

    margin-left: 50px;
  }
}

.mobileHamburgerClose {
  @media (max-width: $lg-breakpoint) {
    display: block;
    margin-top: 33px;
    margin-right: 29px;
  }

  display: none;
}

.sidebarLogoMobile {
  @media (max-width: $lg-breakpoint) {
    display: block;
    width: 128px;
    height: 24px;
    margin-top: 33px;
    margin-bottom: 56px;
    margin-left: 24px;
  }

  display: none;
}

.sidebarLogo {
  @media (max-width: $lg-breakpoint) {
    display: none;
  }

  display: block;
  width: 170px;
  height: 32px;
  margin: 33px auto 56px;
}

.expandBackground {
  @media (max-width: $lg-breakpoint) {
    position: fixed;
    z-index: 90;
    display: block;
    width: 100%;
    height: 100%;
    background: rgba($primary-charcoal, 0.5);
  }

  display: none;
}
</style>
